import { combineGuestBookingWithUserBookingsAndSort } from '@website/components/me/userID/utils/combineGuestBookingWithUserBookingsAndSort';
import { getBookingsFromGuestOnBooking } from '@website/components/me/userID/utils/getUserBookings';
import { UserDashboardQuery } from '@website/types/graphqlOperations';
import { DateTime } from 'luxon';
import Link from 'next/link';
import { useMemo } from 'react';
import { Accordion, Col } from 'react-bootstrap';
import { BookingCards, BookingOrGuest } from '../../BookingCard';
import { bookingsSplit } from './utils/bookingsSplit';

function compareBookingsByDeparture(
  a: BookingOrGuest,
  b: BookingOrGuest,
): number {
  // sort by created date
  return DateTime.fromISO(b.group.start.utc) >
    DateTime.fromISO(a.group.start.utc)
    ? -1
    : 1;
}

function compareBookingsByCreated(
  a: BookingOrGuest,
  b: BookingOrGuest,
): number {
  // sort by newest created date first
  return DateTime.fromISO(b.group.start.utc) >
    DateTime.fromISO(a.group.start.utc)
    ? 1
    : -1;
}

type Props = {
  user: Pick<UserDashboardQuery['user'], 'bookings' | 'guestOnBookings' | 'id'>;
};

/**
 * Customer Dashboard Bookings
 *
 * Displays the bookings of the user in categories previousBookings,
 * currentBookings, upcomingBookings and cancelledBookings,
 *
 */
export default function CustomerDashboardBookings({ user }: Props) {
  const userID = user.id;

  const {
    previousBookings,
    currentBookings,
    upcomingBookings,
    cancelledBookings,
  } = useMemo(() => {
    const bookings = user.bookings?.items || [];

    // Add object property to bookings
    const bookingsWithObjectType = bookings.map((booking) => ({
      ...booking,
      object: 'booking',
    }));

    // get bookings from guestOnBookings, this is so that we can show users
    // bookings they are guests in
    const guestOnBookingsBookings = getBookingsFromGuestOnBooking(
      user.guestOnBookings.items,
    );

    // Combine bookings and guests into one array
    const combinedBookingsAndGuests =
      combineGuestBookingWithUserBookingsAndSort(
        guestOnBookingsBookings,
        bookingsWithObjectType,
      );

    return bookingsSplit(combinedBookingsAndGuests);
  }, [user]);

  return (
    <>
      <Accordion
        defaultActiveKey={
          currentBookings[0] ? 'current-trips' : 'upcoming-trips'
        }
      >
        {currentBookings.sort((a, b) => {
          // sort by created date
          return DateTime.fromISO(b.created)
            .diff(DateTime.fromISO(a.created))
            .toMillis();
        }).length > 0 && (
          <Accordion.Item eventKey="current-trips">
            <Accordion.Header>Current Trips</Accordion.Header>
            <Accordion.Body>
              {currentBookings[0] ? (
                <BookingCards
                  bookings={currentBookings}
                  testId="bookings-current"
                  userID={userID}
                  size="small"
                />
              ) : (
                'You have no current trips.'
              )}
            </Accordion.Body>
          </Accordion.Item>
        )}

        <Accordion.Item eventKey="upcoming-trips">
          <Accordion.Header>Upcoming Trips</Accordion.Header>
          <Accordion.Body>
            {upcomingBookings.sort(compareBookingsByDeparture).length > 0 ? (
              <BookingCards
                bookings={upcomingBookings}
                testId="bookings-upcoming"
                userID={userID}
                size="small"
              />
            ) : (
              <>
                <Col xs={12} className="text-center mb-2">
                  You have no upcoming trips.
                </Col>
                <Col xs={12} className="text-center">
                  <Link href="/trips" className="btn btn-outline-primary">
                    Book a trip now
                  </Link>
                </Col>
              </>
            )}
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="previous-trips">
          <Accordion.Header>Previous Trips</Accordion.Header>
          <Accordion.Body>
            {previousBookings.sort(compareBookingsByCreated).length > 0 ? (
              <BookingCards
                bookings={previousBookings}
                testId="bookings-previous"
                userID={userID}
                size="small"
              />
            ) : (
              'You have no previous trips.'
            )}
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="cancelled-trips">
          <Accordion.Header>Cancelled Trips</Accordion.Header>
          <Accordion.Body>
            {cancelledBookings.sort(compareBookingsByCreated).length > 0 ? (
              <BookingCards
                bookings={cancelledBookings}
                active={false}
                badgeText="Cancelled"
                userID={userID}
                size={'small'}
              />
            ) : (
              'You have no cancelled trips.'
            )}
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    </>
  );
}
